// Render-side sanitiser for RICH TEXT values (a transaction column with
// `edit_mask: richtext`, shown by the `rich_content` block and the form's
// rich-text field).
//
// The engine already sanitises on every write
// (`twasta_runtime_engine/rich_html.py`), so this is defence in depth for
// values that reached the table some other way (business logic, SQL, an
// import). It is the SAME allow-list, and like the Python side it REBUILDS the
// markup from parsed parts: only allow-listed tags/attributes are emitted,
// attribute values are re-escaped, URLs are scheme-checked after entity
// decoding, and all text is re-escaped. No DOM needed, so the site SSR (Node)
// and every browser surface share it.

const ALLOWED: Record<string, readonly string[]> = {
  p: ['class'], br: [], hr: [],
  h2: ['id', 'class'], h3: ['id', 'class'], h4: ['id', 'class'],
  strong: [], b: [], em: [], i: [], u: [], s: [], mark: [],
  code: [], pre: ['class'], blockquote: [],
  ul: [], ol: ['start'], li: [],
  a: ['href', 'title', 'target', 'rel'],
  img: ['src', 'alt', 'title', 'width', 'height'],
  figure: ['class'], figcaption: [],
  table: [], thead: [], tbody: [], tr: [], th: ['colspan', 'rowspan'], td: ['colspan', 'rowspan'],
  sub: [], sup: [], span: ['class'],
}
const ALIASES: Record<string, string> = { h1: 'h2', h5: 'h4', h6: 'h4', del: 's', strike: 's' }
const VOID = new Set(['br', 'hr', 'img'])
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template',
  'svg', 'math', 'head', 'title', 'textarea', 'select',
])
const SAFE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:']
const REL_TOKENS = new Set(['noopener', 'noreferrer', 'nofollow', 'ugc', 'sponsored'])

// A tag, a comment, a doctype/processing instruction, or text.
const TOKEN_RE = /<!--[\s\S]*?(?:-->|$)|<![^>]*>?|<\?[^>]*>?|<\/?([A-Za-z][A-Za-z0-9-]*)((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)*)\s*\/?>/g
const ATTR_RE = /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g

const NAMED: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', colon: ':', tab: '\t', newline: '\n' }

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);?/gi, (m, e: string) => {
    if (e[0] === '#') {
      const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)
      return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : ''
    }
    return NAMED[e.toLowerCase()] ?? m
  })
}

function esc(s: string, attr = false): string {
  let out = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  if (attr) out = out.replace(/"/g, '&quot;')
  return out
}

function safeUrl(raw: string, image: boolean): string | null {
  const v = decodeEntities(raw || '').trim()
  if (!v) return null
  // eslint-disable-next-line no-control-regex
  const probe = v.replace(/[\x00-\x20\x7f]+/g, '').toLowerCase()
  if (/^[/#?]/.test(probe)) return v
  if (!probe.split('/', 1)[0].includes(':')) return v
  if (SAFE_SCHEMES.some(s => probe.startsWith(s))) {
    if (image && (probe.startsWith('mailto:') || probe.startsWith('tel:'))) return null
    return v
  }
  return null
}

function cleanAttr(name: string, value: string): string | null {
  switch (name) {
    case 'href': return safeUrl(value, false)
    case 'src': return safeUrl(value, true)
    case 'class': return /^[A-Za-z0-9 _-]{0,120}$/.test(value) ? value : null
    case 'id': return /^[A-Za-z][A-Za-z0-9_-]{0,80}$/.test(value) ? value : null
    case 'width': case 'height': case 'colspan': case 'rowspan': case 'start':
      return /^\d{1,5}$/.test(value.trim()) ? value.trim() : null
    case 'target': return value.trim() === '_blank' ? '_blank' : null
    case 'rel': {
      const t = value.split(/\s+/).filter(x => REL_TOKENS.has(x)).join(' ')
      return t || null
    }
    default: return decodeEntities(value).slice(0, 500)
  }
}

/** Reduce `html` to the allow-listed rich-text subset. Always returns a
 *  string that is safe to hand to `dangerouslySetInnerHTML`. */
export function sanitizeRichHtml(html: unknown): string {
  if (html == null) return ''
  const src = String(html)
  if (!src.trim()) return ''
  const out: string[] = []
  const stack: string[] = []
  let dropTag: string | null = null
  let dropDepth = 0
  let last = 0
  TOKEN_RE.lastIndex = 0
  let m: RegExpExecArray | null
  const text = (t: string) => { if (!dropDepth && t) out.push(esc(decodeEntities(t))) }

  while ((m = TOKEN_RE.exec(src)) !== null) {
    text(src.slice(last, m.index))
    last = TOKEN_RE.lastIndex
    const rawTag = m[1]
    if (!rawTag) continue                         // comment / doctype / PI
    const closing = m[0][1] === '/'
    let tag = rawTag.toLowerCase()

    if (dropDepth) {
      if (tag === dropTag) dropDepth += closing ? -1 : 1
      if (!dropDepth) dropTag = null
      continue
    }
    if (DROP_WITH_CONTENT.has(tag)) {
      if (!closing) { dropTag = tag; dropDepth = 1 }
      continue
    }
    tag = ALIASES[tag] ?? tag
    const allowed = ALLOWED[tag]
    if (!allowed) continue

    if (closing) {
      if (VOID.has(tag) || !stack.includes(tag)) continue
      while (stack.length) {
        const open = stack.pop() as string
        out.push(`</${open}>`)
        if (open === tag) break
      }
      continue
    }

    const parts = [tag]
    let hasRel = false
    let blank = false
    let hasSrc = false
    ATTR_RE.lastIndex = 0
    let a: RegExpExecArray | null
    const attrs = m[2] || ''
    while ((a = ATTR_RE.exec(attrs)) !== null) {
      const name = a[1].toLowerCase()
      if (!allowed.includes(name)) continue
      const value = a[2] ?? a[3] ?? a[4] ?? ''
      const clean = cleanAttr(name, value)
      if (clean == null) continue
      if (name === 'rel') hasRel = true
      if (name === 'target') blank = true
      if (name === 'src') hasSrc = true
      parts.push(`${name}="${esc(clean, true)}"`)
    }
    if (tag === 'img' && !hasSrc) continue
    if (tag === 'a' && blank && !hasRel) parts.push('rel="noopener noreferrer"')
    out.push(`<${parts.join(' ')}>`)
    if (!VOID.has(tag)) stack.push(tag)
  }
  text(src.slice(last))
  while (stack.length) out.push(`</${stack.pop()}>`)
  return out.join('')
}

/** Visible text of a rich-text value — list cells, excerpts, reading time. */
export function richPlainText(html: unknown): string {
  if (html == null) return ''
  return decodeEntities(String(html).replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim()
}

export function slugifyHeading(text: string): string {
  return text.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
}

export interface RichTocEntry { id: string; text: string; level: number }

export interface PreparedRichContent {
  html: string
  toc: RichTocEntry[]
  wordCount: number
  readingMinutes: number
}

/**
 * Sanitise and decorate a rich-text body for display: every h2–h4 gets a
 * stable `id` (deep links + table of contents), images get `loading="lazy"`
 * (all but the first — it is usually the LCP image) and `decoding="async"`,
 * and external links open safely. Pure string work, identical on SSR and
 * in the browser.
 */
export function prepareRichContent(
  html: unknown,
  opts: { externalLinksNewTab?: boolean; nofollowExternal?: boolean; siteOrigin?: string } = {},
): PreparedRichContent {
  const clean = sanitizeRichHtml(html)
  const toc: RichTocEntry[] = []
  const used = new Set<string>()
  let imgIndex = 0

  let out = clean.replace(/<(h[234])((?:\s[^>]*)?)>([\s\S]*?)<\/\1>/g, (_m, tag: string, attrs: string, inner: string) => {
    const text = richPlainText(inner)
    const existing = /\sid="([^"]*)"/.exec(attrs)?.[1]
    let id = existing || slugifyHeading(text) || `section-${toc.length + 1}`
    if (!existing) {
      let n = 2
      const base = id
      while (used.has(id)) id = `${base}-${n++}`
    }
    used.add(id)
    toc.push({ id, text, level: Number(tag[1]) })
    const rest = attrs.replace(/\sid="[^"]*"/, '')
    return `<${tag} id="${esc(id, true)}"${rest}>${inner}</${tag}>`
  })

  out = out.replace(/<img\s([^>]*)>/g, (_m, attrs: string) => {
    const lazy = imgIndex++ === 0 ? '' : ' loading="lazy"'
    return `<img ${attrs}${lazy} decoding="async">`
  })

  if (opts.externalLinksNewTab || opts.nofollowExternal) {
    const origin = (opts.siteOrigin || '').toLowerCase()
    out = out.replace(/<a\s([^>]*)>/g, (full, attrs: string) => {
      const href = /href="([^"]*)"/.exec(attrs)?.[1] ?? ''
      const external = /^https?:\/\//i.test(href) && !(origin && href.toLowerCase().startsWith(origin))
      if (!external) return full
      let a = attrs
      if (opts.externalLinksNewTab && !/\starget=/.test(` ${a}`)) a += ' target="_blank"'
      const rel = new Set((/rel="([^"]*)"/.exec(a)?.[1] ?? '').split(/\s+/).filter(Boolean))
      if (/\starget="_blank"/.test(` ${a}`)) { rel.add('noopener'); rel.add('noreferrer') }
      if (opts.nofollowExternal) rel.add('nofollow')
      a = a.replace(/\s?rel="[^"]*"/, '') + ` rel="${[...rel].join(' ')}"`
      return `<a ${a.trim()}>`
    })
  }

  const wordCount = richPlainText(clean).split(/\s+/).filter(Boolean).length
  return { html: out, toc, wordCount, readingMinutes: Math.max(1, Math.round(wordCount / 225)) }
}
